import { useRef, useState } from 'react';

import { motion, useReducedMotion } from 'framer-motion';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { SheetBackButton } from '@nesso/ui/primitives/sheet';

import type { ClientListItem } from '@/entities/client';

import {
    SEARCH_FIELD_COLLAPSED_WIDTH_PX,
    SEARCH_FIELD_EXPAND_TRANSITION,
    SEARCH_FIELD_EXPANDED_WIDTH_PX,
    SEARCH_FIELD_REDUCED_MOTION_TRANSITION,
} from '../config/search';
import { SearchClientField } from './search-client-navigation/search-client-field';
import { SearchClientMobileView } from './search-client-navigation/search-client-mobile-view';
import { SearchClientTrigger } from './search-client-navigation/search-client-trigger';

type Props = {
    hasEhrAccess: boolean;
    onSelectClient: (client: ClientListItem) => void;
};

export function SearchClientNavigation({ hasEhrAccess, onSelectClient }: Props) {
    return hasEhrAccess ? <SearchClientNavigationBody onSelectClient={onSelectClient} /> : null;
}

function SearchClientNavigationBody({ onSelectClient }: Pick<Props, 'onSelectClient'>) {
    const isMobile = useBreakpointDown('md');
    const prefersReducedMotion = useReducedMotion();
    const [isFieldOpen, setIsFieldOpen] = useState(false);
    const [openCount, setOpenCount] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const expand = () => {
        if (isFieldOpen) return;
        setIsFieldOpen(true);
        setOpenCount((count) => count + 1);
    };
    const collapse = () => setIsFieldOpen(false);

    const handleSelectClient = (client: ClientListItem) => {
        collapse();
        onSelectClient(client);
    };

    if (isMobile) {
        return (
            <>
                <SearchClientTrigger onClick={expand} />
                <SearchClientMobileView
                    open={isFieldOpen}
                    onOpenChange={(open) => (open ? expand() : collapse())}
                    inputRef={inputRef}
                    field={
                        <SearchClientField
                            key={openCount}
                            ref={inputRef}
                            variant="navigation"
                            hasEhrAccess
                            layout="panel"
                            onSelectClient={handleSelectClient}
                            onClose={collapse}
                            onAddNewClient={collapse}
                            leading={
                                <SheetBackButton onClick={collapse} aria-label="Close search" />
                            }
                        />
                    }
                />
            </>
        );
    }

    const transition = prefersReducedMotion
        ? SEARCH_FIELD_REDUCED_MOTION_TRANSITION
        : SEARCH_FIELD_EXPAND_TRANSITION;

    return (
        <motion.div
            animate={{
                width: isFieldOpen
                    ? SEARCH_FIELD_EXPANDED_WIDTH_PX
                    : SEARCH_FIELD_COLLAPSED_WIDTH_PX,
            }}
            transition={transition}
            onAnimationComplete={() => {
                if (isFieldOpen) {
                    inputRef.current?.focus();
                }
            }}
        >
            <motion.div
                key={isFieldOpen ? 'field' : 'trigger'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={transition}
            >
                {isFieldOpen ? (
                    <SearchClientField
                        ref={inputRef}
                        variant="navigation"
                        hasEhrAccess
                        layout="dropdown"
                        onSelectClient={handleSelectClient}
                        onClose={collapse}
                        onAddNewClient={collapse}
                    />
                ) : (
                    <SearchClientTrigger onClick={expand} />
                )}
            </motion.div>
        </motion.div>
    );
}
